import { Injectable, Injector } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpDataService } from './http-data.service';
import JSZip from 'jszip';
import * as JSZipUtils from 'jszip-utils';
import { saveAs } from 'file-saver';
import { environment } from 'environments/environment';

const TYPES = {
  image: ['jpg', 'jpeg', 'gif', 'png'],
  pdf: ['pdf'],
  sound: ['mp3', 'm4a', 'wav', 'mp4'],
  video: ['mp4', 'mov', 'webm', 'MPG']
};

@Injectable()
export class CourseDocumentsService extends HttpDataService {
  constructor(protected injector: Injector) {
    super(injector, '/');
  }

  getCourseDocuments(courseId, lang) {
    const url = `${this.baseUrl}courses/${courseId}/documents?lang=${lang}&getBook=true`;

    return lastValueFrom(this.http.get(url))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  htmlToBase64WithTemplate(template: string, content: any): Promise<any> {
    return lastValueFrom(this.http.post(`${this.baseUrl}reports/pdf`, { template, content }, this.getRequestOptions()))
      .then((response) => response)
      .catch(this.handleError);
  }

  async downloadDocumentation(documents, courseName, lang) {
    const zip = new JSZip();
    const downloadableDocuments = documents.map((doc) => {
      if (doc.type === 'url') {
        const html = `<a href="${doc.url}">${doc.url}</a>`;
        doc.content = {
          pageContent: html
        };
        doc.template = 'lesson';
        doc.kind = 'courseDocument';
        doc.type = 'pdf';
        delete doc.url;
      }
      return doc;
    });

    for (const index in downloadableDocuments) {
      const { url: docUrl, type, kind, name, template, content, courseName } = downloadableDocuments[index];

      let url: string;
      if (template) {
        const response = await this.htmlToBase64WithTemplate(template, content);
        url = `data:application/pdf;base64, ${response.file}`;
      } else {
        url = this.getUrl(docUrl, kind);
      }

      await new Promise<void>((resolve) => {
        JSZipUtils.getBinaryContent(url, async function (err, data) {
          if (err) {
            // We do not want to throw an error if a document no longer exists in S3
            // eslint-disable-next-line no-console
            console.error(err);
          }

          const documentName = name
            ? name
            : ['book', 'beginnersGuide'].includes(kind)
            ? `${courseName}_${kind}`
            : `${kind}${index}`;
          const fileExtension = url.split('.').pop();
          const typeName = TYPES[type] ? '.' + (TYPES[type].find((t) => t === fileExtension) || TYPES[type][0]) : '';
          const fileName = `${documentName}${typeName}`;

          zip.file(fileName, data, { binary: true });
          resolve();
        });
      });
    }

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${courseName}_docs_${lang}.zip`);
    });
  }
  getUrl(url, kind) {
    if (url.startsWith('http' || url.startsWith('data:'))) {
      return url;
    }
    const streamingTypes = ['.mp3', '.m4a', '.wav', '.mp4', '.mov', '.webm', '.MPG'];
    const host = streamingTypes.find((type) => url.includes(type))
      ? environment.STREAMING_HOST
      : environment.RESOURCES_HOST;

    return url.indexOf(host) !== 0 && kind === 'courseDocument' ? host + url : url;
  }
}
